/**
 * Frogger game configuration — endless scrolling mode.
 */

export const FROGGER_CONFIG = {
    CELL_SIZE: 70,
    COLS: 27,
    PLAYER_SIZE: 64,

    PLAYER_SCREEN_ROW: 2.5,
    CAMERA_LERP: 0.12,
    VISIBLE_BUFFER: 4,

    /** Permanent top stat bar (px tall). Must clear the chip+button row. */
    TOP_BAR_H: 110,
    /** Permanent bottom strip (px tall). Holds power-up cards + start grass. */
    BOTTOM_BAR_H: 272,
    /** How many cells of clearance above the bottom bar the player should sit
     * at while the camera is scrolling. Larger = player floats higher on the road. */
    PLAYER_SCROLL_CELLS_ABOVE_BOTTOM: 1.5,
    /** Pixel height of the museum box (NOT counting the +1 cell lawn that
     * extends below the box down to the win row's bottom). Sized so the
     * default `muzeum.png` (1562x826, aspect 1.89) — once cover-scaled to
     * the full grid width (27 * 70 = 1890 px) — fits its natural 1000 px
     * rendered height entirely inside `box (930) + lawnExtra (70 = 1 cell)`,
     * with NO upward shift in `_positionFinishSprite`. Result: the full
     * museum (sign, pediment, body, door, lawn) renders without any
     * top/bottom crop. The building's BOTTOM (lawn) always lines up with
     * the bottom edge of the win row. */
    FINISH_BUILDING_HEIGHT_PX: 930,

    SAFE_INTERVAL_RANGE: [4, 10],

    /** Rows reserved as the decorative start safe zone (must fit the height of
     * `frogger.bottom`). These rows render no background fill, no random decor,
     * and no traffic — the bottom strip image provides the visuals. */
    START_SAFE_ROWS: 3,

    /** Native asset sizes — bikes/cars/trucks/bus all fit a single 70px cell row. */
    VEHICLE_TYPES: [
        { type: 'bike',  w: 62,  h: 70 },
        { type: 'car',   w: 128, h: 70 },
        { type: 'truck', w: 149, h: 70 },
        { type: 'bus',   w: 208, h: 70 },
    ],

    SPEED_RANGE: [1.2, 2.6],
    SPAWN_RATE_RANGE: [90, 160],

    SPAWN_JITTER: 35,
    MIN_VEHICLE_GAP: 150,
    SPAWN_EDGE_CLEARANCE: 180,
    MAX_VEHICLES_PER_ROW: 6,

    INITIAL_VEHICLES_RANGE: [3, 4],

    SPEED_INCREASE_PER_10_ROWS: 0.06,
    MIN_SPAWN_RATE: 55,

    POINTS_PER_NEW_ROW: 10,
    INITIAL_LIVES: 3,

    COLOR_SAFE_ZONE: 0xb2c63c,
    COLOR_ROAD: 0x3a91dc,
    COLOR_LANE_LINE: 0xffffff,

    // ── Collectibles ───────────────────────────────────
    /** Chance a road row gets a bonus coin (0-1) */
    COIN_CHANCE: 0.35,
    COIN_POINTS: 25,
    COIN_COLOR: 0xfbbf24,
    COIN_RADIUS: 12,

    /** Mandatory stars — appear every N road rows */
    STAR_INTERVAL_RANGE: [3, 5],
    STAR_POINTS: 100,
    STAR_COLOR: 0xec4899,
    STAR_RADIUS: 16,
    /** Rows the player can pass a star before it counts as "missed" */
    STAR_MISS_ROWS: 6,
    /** Blink fast when player is this many rows ahead */
    STAR_WARN_ROWS: 3,

    // ── Speed multipliers ───────────────────────────────
    SPEED_MULTIPLIERS: { slow: 0.6, normal: 1.0, fast: 1.5 },

    // ── Density multipliers ─────────────────────────────
    // spawnRate is divided by spawnRateDiv (higher = more frequent),
    // maxVehicles is multiplied by maxVehicleMul, initial vehicle count by
    // initialMul, and the physical spacing (MIN_VEHICLE_GAP and
    // SPAWN_EDGE_CLEARANCE) is multiplied by gapMul. Without gapMul the
    // 1890px road would saturate at ~6 cars and normal/many/max would all
    // look identical.
    DENSITY_PROFILES: {
        few:    { spawnRateDiv: 0.55, maxVehicleMul: 0.5, initialMul: 0.4, gapMul: 1.7  },
        normal: { spawnRateDiv: 1.0,  maxVehicleMul: 1.0, initialMul: 1.0, gapMul: 1.0  },
        many:   { spawnRateDiv: 1.6,  maxVehicleMul: 1.8, initialMul: 1.5, gapMul: 0.6  },
        max:    { spawnRateDiv: 2.6,  maxVehicleMul: 2.6, initialMul: 2.2, gapMul: 0.38 },
    },

    // ── Birds ───────────────────────────────────────────
    BIRD_SPAWN_INTERVAL: [180, 360],
    BIRD_SPEED: 3.0,
    BIRD_SIZE: 60,
    BIRD_COLOR: 0xdc2626,

    // ── Abilities ───────────────────────────────────────
    ABILITY_SPAWN_CHANCE: 0.12,
    ABILITY_TYPES: ['dash', 'slowtime', 'bomb'],
    /** Legacy single value — kept for code paths that read a fixed distance. */
    DASH_ROWS: 3,
    /** Inclusive [min, max] random range of rows a single dash covers.
     * Used as the fallback when no per-user override is stored under
     * `frogger.settings → dashRowsMin / dashRowsMax`. */
    DASH_ROWS_RANGE: [3, 15],
    SLOWTIME_DURATION: 300,
    SLOWTIME_FACTOR: 0.5,
    BOMB_RADIUS: 1,
    ABILITY_COLORS: { dash: 0x06b6d4, slowtime: 0x8b5cf6, bomb: 0xf97316 },
    ABILITY_RADIUS: 14,

    // ── Lane changing ───────────────────────────────────
    LANE_CHANGE_CHANCE: 0.0008,
    LANE_CHANGE_FRAMES: 30,
    LANE_CHANGE_COOLDOWN: 300,
};
